import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { SupportApiError, SupportApiService } from '../services/support-api.service';
import { SupportChatService, newClientId } from '../services/support-chat.service';
import {
  SupportAttachmentNotice,
  SupportDraftAttachment,
  SupportUploadQueue,
  fileIcon,
  fileSize,
  hasFiles,
  namePastedImage,
  rejectionNotice,
  uploadFailureKey,
} from '../services/support-upload-queue';
import { SUPPORT_ATTACHMENT_LIMITS, SUPPORT_FEATURES } from '../support-system.config';
import {
  IssueReportDevice,
  IssueReportNudge,
  IssueReportNudgeResult,
  IssueReportRequest,
  IssueReportResponse,
  SupportDiagnosticsSnapshot,
  SupportErrorCode,
} from '../support-system.models';
import { NUDGE_PRODUCT, productForUrl, reportProducts } from './report-products';

export interface SupportReportDialogData {
  /** Omitted when the account has a single shop. */
  target?: string;
  /** Name the shop in the header (the account has several). */
  showTarget: boolean;
  /** Activity history frozen when "Report an issue" was clicked. */
  snapshot: SupportDiagnosticsSnapshot;
}

type Step = 'form' | 'confirm' | 'done';

const MAX_MESSAGE_LENGTH = 4000;
const MAX_NUDGES = 50;
const MAX_SUGGESTIONS = 50;
/**
 * Typing or pasting any of these ends a nudge ID. Not spaces: the field also
 * searches by name ("Free shipping bar").
 */
const NUDGE_ID_SEPARATORS = /[,;\n]+/;

const ERROR_KEYS: Partial<Record<SupportErrorCode, string>> = {
  OFFLINE: 'support_chat.report_error_network',
  TIMEOUT: 'support_chat.report_error_network',
  RATE_LIMITED: 'support_chat.report_error_rate_limited',
  INVALID_REQUEST: 'support_chat.report_error_invalid',
  FORBIDDEN: 'support_chat.report_error_forbidden',
};

/**
 * "Report an issue": product, device, what happened, files and nudges to
 * turn off, sent in one go to `/issue-reports`. The backend turns the
 * nudges off, opens a ticket for the team and emails the client; there is
 * no chat to follow.
 */
@Component({
  selector: 'app-support-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextFieldModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './support-report-dialog.component.html',
  styleUrls: ['./support-report-dialog.component.css'],
})
export class SupportReportDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<SupportReportDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<SupportReportDialogComponent>);
  private api = inject(SupportApiService);
  private chat = inject(SupportChatService);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);

  @ViewChild('filePicker') filePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('nudgeInput') nudgeInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) nudgeAutocomplete?: MatAutocompleteTrigger;

  readonly maxLength = MAX_MESSAGE_LENGTH;
  readonly maxFileMb = SUPPORT_ATTACHMENT_LIMITS.maxBytes / (1024 * 1024);
  readonly fileAccept = SUPPORT_ATTACHMENT_LIMITS.mimeTypes.join(',');
  readonly devices: IssueReportDevice[] = ['desktop', 'tablet', 'mobile'];
  readonly fileIcon = fileIcon;
  readonly fileSize = fileSize;

  /** Only the products the user can open, preselected from the page. */
  readonly products = reportProducts(
    this.localStorageService.get<string[]>('privileges') || [],
  );
  /** Empty when the page isn't one of them: the user picks. */
  product: string | null = productForUrl(this.products, this.router.url);
  device: IssueReportDevice | null = null;
  message = '';

  readonly files = new SupportUploadQueue((file) => this.chat.uploadReportAttachment(file));
  readonly attachmentNotice = signal<SupportAttachmentNotice | null>(null);
  readonly dragging = signal(false);
  private dragDepth = 0;

  /* Live nudges, loaded each time the form opens (they change often). */
  readonly nudgesState = signal<'loading' | 'ready' | 'error'>('loading');
  readonly canStopNudges = signal(false);
  readonly nudges = signal<IssueReportNudge[]>([]);
  /** The nudges the report is about. Only ids of live nudges get in. */
  readonly nudgeIds = signal<string[]>([]);
  /** "I want a nudge to be stopped": turn `nudgeIds` off right away. */
  stopNudges = false;
  /** What is typed in the nudge field, not yet an id. */
  readonly nudgeQuery = signal('');
  /** Typed text that isn't a live nudge's id. */
  readonly invalidNudgeInput = signal<string | null>(null);
  /** Live nudges not chosen yet that match the typed text (id or name). */
  readonly nudgeSuggestions = computed(() => {
    const chosen = this.nudgeIds();
    const query = this.nudgeQuery().trim().toLowerCase();
    return this.nudges()
      .filter((n) => !chosen.includes(n.id))
      .filter(
        (n) =>
          !query ||
          n.id.toLowerCase().includes(query) ||
          !!n.name?.toLowerCase().includes(query),
      )
      .slice(0, MAX_SUGGESTIONS);
  });
  private nudgesRequest?: Subscription;

  readonly step = signal<Step>('form');
  readonly sending = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly result = signal<IssueReportResponse | null>(null);
  /** The last attempt: a retry of the same report reuses its id. */
  private attempt: { id: string; content: string } | null = null;

  ngOnInit(): void {
    this.loadNudges();
  }

  ngOnDestroy(): void {
    this.nudgesRequest?.unsubscribe();
    this.files.clear();
  }

  /* ------------------------------- Nudges ------------------------------- */

  loadNudges(): void {
    this.nudgesState.set('loading');
    this.nudgesRequest?.unsubscribe();
    this.nudgesRequest = this.api.issueReportNudges(this.data.target).subscribe({
      next: ({ canStopNudges, nudges }) => {
        this.canStopNudges.set(canStopNudges);
        this.nudges.set(nudges);
        // Gone since the last load (turned off by someone else).
        this.setNudgeIds(this.nudgeIds().filter((id) => nudges.some((n) => n.id === id)));
        this.nudgesState.set('ready');
      },
      error: () => this.nudgesState.set('error'),
    });
  }

  /** The "Nudges" field is for Nudge reports, whoever sends them. */
  get showNudges(): boolean {
    return this.product === NUDGE_PRODUCT;
  }

  /** Under the field, to admins and sub-masters, once a nudge is named. */
  get showStopNudges(): boolean {
    return this.showNudges && this.canStopNudges() && !!this.nudgeIds().length;
  }

  nudgeName(id: string): string {
    return this.nudges().find((n) => n.id === id)?.name || id;
  }

  onNudgeQuery(value: string): void {
    this.invalidNudgeInput.set(null);
    // A separator typed or pasted turns everything before it into ids.
    if (NUDGE_ID_SEPARATORS.test(value)) {
      const parts = value.split(NUDGE_ID_SEPARATORS);
      const rest = parts.pop() || '';
      if (!this.addTypedIds(parts, rest)) this.closeSuggestions();
      return;
    }
    this.nudgeQuery.set(value);
  }

  onNudgeKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // A highlighted suggestion is picked by the autocomplete itself.
      if (this.nudgeAutocomplete?.panelOpen && this.nudgeAutocomplete.activeOption) return;
      event.preventDefault();
      if (!this.commitNudgeQuery()) this.closeSuggestions();
    } else if (event.key === 'Backspace' && !this.nudgeQuery()) {
      this.setNudgeIds(this.nudgeIds().slice(0, -1));
    }
  }

  /**
   * Leaving the field checks what was typed. With the suggestions open the
   * blur may be a click on one of them, so the check waits for them to close.
   */
  onNudgeBlur(): void {
    if (this.nudgeAutocomplete?.panelOpen) return;
    this.commitNudgeQuery();
  }

  /**
   * `closed` fires while the panel is still closing; the check runs after,
   * so nothing re-enters the autocomplete mid-close.
   */
  onNudgeSuggestionsClosed(): void {
    setTimeout(() => {
      if (document.activeElement !== this.nudgeInput?.nativeElement) this.commitNudgeQuery();
    });
  }

  pickNudge(id: string): void {
    this.addTypedIds([id], '');
  }

  removeNudge(id: string): void {
    this.setNudgeIds(this.nudgeIds().filter((n) => n !== id));
    this.nudgeInput?.nativeElement.focus();
  }

  /**
   * Several ids may be typed with spaces between them ("1.1.2 3.0.1").
   * False when what was typed isn't an id.
   */
  private commitNudgeQuery(): boolean {
    const value = this.nudgeQuery().trim();
    const words = value.split(/\s+/);
    const allIds = words.every((w) => this.nudges().some((n) => n.id === w));
    return this.addTypedIds(allIds ? words : [value], '');
  }

  /** The open suggestions would cover the error under the field. */
  private closeSuggestions(): void {
    if (this.nudgeAutocomplete?.panelOpen) this.nudgeAutocomplete.closePanel();
  }

  /**
   * Adds the values that are exactly a live nudge's id. Anything else stays
   * in the field, flagged, so it can be corrected. False if anything did.
   */
  private addTypedIds(values: string[], rest: string): boolean {
    const live = new Set(this.nudges().map((n) => n.id));
    const ids = [...this.nudgeIds()];
    const invalid: string[] = [];
    for (const raw of values) {
      const value = raw.trim();
      if (!value) continue;
      if (!live.has(value)) invalid.push(value);
      else if (!ids.includes(value) && ids.length < MAX_NUDGES) ids.push(value);
    }
    this.setNudgeIds(ids);
    this.invalidNudgeInput.set(invalid.length ? invalid.join(', ') : null);
    this.setNudgeQuery([...invalid, rest].filter(Boolean).join(' '));
    return !invalid.length;
  }

  /** With no nudge left, there is nothing to stop. */
  private setNudgeIds(ids: string[]): void {
    this.nudgeIds.set(ids);
    if (!ids.length) this.stopNudges = false;
  }

  /**
   * Written to the element too: with ngModel, a paste that ends up as the
   * same text again would leave the pasted text on screen.
   */
  private setNudgeQuery(value: string): void {
    this.nudgeQuery.set(value);
    const input = this.nudgeInput?.nativeElement;
    if (input) input.value = value;
  }

  /* -------------------------------- Send -------------------------------- */

  /** The nudges sent with the report (only for a Nudge report). */
  private get reportNudgeIds(): string[] {
    return this.showNudges ? this.nudgeIds() : [];
  }

  /** Whether sending turns `reportNudgeIds` off. */
  get willStopNudges(): boolean {
    return this.showStopNudges && this.stopNudges;
  }

  get canSubmit(): boolean {
    return (
      !!this.product &&
      !!this.message.trim() &&
      this.files.settled() &&
      !this.sending() &&
      // Something typed in "Nudges" that isn't an id yet must be fixed first.
      !(this.showNudges && this.nudgeQuery().trim())
    );
  }

  /** Turning nudges off happens right away, so it is confirmed first. */
  submit(): void {
    if (!this.canSubmit) return;
    if (this.willStopNudges && this.step() === 'form') {
      this.step.set('confirm');
      return;
    }
    this.send();
  }

  backToForm(): void {
    this.step.set('form');
  }

  private send(): void {
    const nudgeIds = this.reportNudgeIds;
    // Optional fields only when set: the validation is strict.
    const report: Omit<IssueReportRequest, 'clientRequestId'> = {
      ...(this.data.target ? { target: this.data.target } : {}),
      product: this.product!,
      ...(this.device ? { device: this.device } : {}),
      message: this.message.trim(),
      ...(this.files.count() ? { attachmentIds: this.files.items().map((i) => i.attachment!.id) } : {}),
      ...(nudgeIds.length ? { nudgeIds } : {}),
      ...(this.willStopNudges ? { stopNudges: true } : {}),
    };
    const content = JSON.stringify(report);
    if (this.attempt?.content !== content) this.attempt = { id: newClientId(), content };

    this.sending.set(true);
    this.errorKey.set(null);
    this.dialogRef.disableClose = true;
    this.api
      .submitIssueReport({
        ...report,
        clientRequestId: this.attempt.id,
        diagnostics: this.data.snapshot,
      })
      .subscribe({
        next: (result) => {
          this.sending.set(false);
          this.dialogRef.disableClose = false;
          this.attempt = null;
          this.result.set(result);
          this.step.set('done');
        },
        error: (error: SupportApiError) => {
          this.sending.set(false);
          this.dialogRef.disableClose = false;
          this.step.set('form');
          this.errorKey.set(ERROR_KEYS[error.code] || 'support_chat.report_error_generic');
          // Not a retry of the same thing any more: the next try gets a new id.
          if (error.code === 'CONFLICT') this.attempt = null;
          // A chosen nudge may have gone offline meanwhile.
          if (error.code === 'INVALID_REQUEST' && nudgeIds.length) this.loadNudges();
        },
      });
  }

  /** The done screen's lists, in the order they were chosen. */
  readonly outcomes = computed(() => {
    const nudges = this.result()?.nudges || [];
    const of = (result: IssueReportNudgeResult) =>
      nudges.filter((n) => n.result === result).map((n) => n.id);
    return {
      off: [...of('turned_off'), ...of('already_off')],
      failed: of('failed'),
    };
  });

  close(): void {
    if (!this.sending()) this.dialogRef.close();
  }

  /* -------------------------------- Files -------------------------------- */

  get canAttach(): boolean {
    return SUPPORT_FEATURES.attachments && this.chat.attachmentsAvailable();
  }

  pickFiles(): void {
    this.filePicker?.nativeElement.click();
  }

  onFilesPicked(input: HTMLInputElement): void {
    this.addFiles(Array.from(input.files || []));
    input.value = '';
  }

  /** Screenshots pasted into the description become attachments. */
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files || []);
    if (!files.length || !this.canAttach) return;
    event.preventDefault();
    this.addFiles(files.map(namePastedImage));
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canAttach || this.step() !== 'form' || !hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth++;
    this.dragging.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.dragging()) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(): void {
    if (!this.dragging()) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (!this.dragDepth) this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.dragging()) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  removeFile(localId: string): void {
    this.files.remove(localId);
    this.attachmentNotice.set(null);
  }

  uploadFailureKey(item: SupportDraftAttachment): string {
    return uploadFailureKey(item);
  }

  private addFiles(files: File[]): void {
    if (!files.length) return;
    this.attachmentNotice.set(rejectionNotice(this.files.add(files)));
  }
}
